import {put, call} from 'redux-saga/effects';
import Blackout from "../../entities/blackout";
import * as Actions from "../actions/blackoutActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type BlackoutAction = {
    blackoutId: string;
    blackout: Blackout;
    blackouts: Blackout[];
};

export function* fetchOneBlackout(action: BlackoutAction) {
	try {
		const blackout: Blackout = yield call(api.findOne, 'blackout', action.blackoutId);

		// Now we can update the user interface
		yield put({type: Actions.BLACKOUT_FETCH_ONE_SUCCESS, blackoutId: action.blackoutId, blackout});
	}
	catch(err) {
		yield put({type: Actions.BLACKOUT_FETCH_ONE_FAILURE, blackoutId: action.blackoutId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {BlackoutAction} action
 */
export function* saveBlackout(action: BlackoutAction) {
    // console.debug('⮂ BLACKOUT_SAVE saga', action.blackout?.id);
    
	if (action.blackout === undefined || action.blackout === null) {
		console.error('saveBlackout received a null');
		throw new Error('saveBlackout received a null');
	}

	if (!(action.blackout instanceof Blackout)) {
		action.blackout = new Blackout(action.blackout);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'blackout', action.blackout);
		if (addResult.isErr()) {
			console.error(`⮂ saveBlackout: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.BLACKOUT_SAVE_FAILURE, blackout: action.blackout, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.BLACKOUT_SAVE, blackout: action.blackout});

		// Delete this entry from the cache (if there is one). Use uncacheByEntityId so we
		// catch entries regardless of which parentId the writer used (zero for grid views,
		// topLevelId for sub-task views, etc.) — the prior uncacheItem call passed entityId
		// as parentId, which never matched what writers store under, so entries leaked.
		yield call(window.storageWorker.uncacheByEntityId, getCurrentFirmId(), getCurrentUserId(), 'blackout', action.blackout.id);

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		yield put({type: Actions.BLACKOUT_SAVE_FAILURE, blackout: action.blackout, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {BlackoutAction} action
 * @param {Blackout[]} action.blackouts
 */
export function* saveManyBlackouts(action: BlackoutAction) {
    // console.debug('⮂ BLACKOUT_SAVE_MANY saga', action.blackouts.map(blackout => blackout.id).join(','));
    
    if (!Array.isArray(action.blackouts)) {
        console.error('saveManyBlackouts expected an array, received', action.blackouts);
        throw new Error('saveManyBlackouts expected an array');
    }

	try {
		const toSave: Blackout[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.blackouts.length; i++) {
			const blackout = action.blackouts[i];
			blackout.persisted = false;
			toSave.push(blackout);
		}

		const addResult = yield call(idb.add, 'blackout', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyBlackouts: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.BLACKOUT_SAVE_MANY_FAILURE, blackouts: action.blackouts, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.BLACKOUT_SAVE_MANY, blackouts: toSave});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.BLACKOUT_SAVE_MANY_FAILURE, blackouts: action.blackouts, status: 500, err});

		console.error(`Error saving ${action.blackouts.length} blackouts to database`, err);
	}
}

/**
 * @param {BlackoutAction} action
 */
export function* removeBlackout(action: BlackoutAction) {
    const userId = getCurrentUserId();
	if (!userId || action.blackout === undefined || action.blackout === null || !(action.blackout instanceof Blackout)) {
			console.error('removeBlackout expected type Blackout, received', action.blackout);
			throw new Error('removeBlackout expected type Blackout');
		}

	try {
		// Mark as deleted
		action.blackout.status = 'D';
		action.blackout.addedOn = newHRTimestamp();
		action.blackout.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'blackout', action.blackout);
		if (addResult.isErr()) {
			console.error(`⮂ removeBlackout: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.BLACKOUT_REMOVE_FAILURE, blackout: action.blackout, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.BLACKOUT_REMOVE, blackout: action.blackout});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.BLACKOUT_REMOVE_FAILURE, blackout: action.blackout, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this Blackout.
	*
	* @param {BlackoutAction} action
	*/
export function* restoreBlackout(action: BlackoutAction) {
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreBlackout expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.blackout === undefined || action.blackout === null) {
        console.error('restoreBlackout expected a Blackout, received null');
        throw new Error('restoreBlackout expected a Blackout, received null');
    }
    else if (!(action.blackout instanceof Blackout)) action.blackout = new Blackout(action.blackout); 
    
	try {
		// Mark as restored
		action.blackout.status = 'U';
		action.blackout.addedOn = newHRTimestamp();
		action.blackout.addedBy = userId;
		action.blackout.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'blackout', action.blackout);
		if (addResult.isErr()) {
			console.error(`⮂ restoreBlackout: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.BLACKOUT_RESTORE_FAILURE, blackout: action.blackout, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.BLACKOUT_RESTORE, blackout: action.blackout});
	}
	catch(err) {
		yield put({type: Actions.BLACKOUT_RESTORE_FAILURE, blackout: action.blackout, status: 500, err});

		console.error('Error restoring from database', err);
	}
}